import React, { useState, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { BACKEND_URL, getUsers } from '../../api/api';

/**
 * Componente para escanear formularios manuscritos de fichaje
 * Permite al admin tomar/subir foto del formulario y procesarlo con IA
 */
export default function EscaneoFormularioManuscrito({ onVerFichajes }) {
  const { token } = React.useContext(AuthContext);
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editableFichajes, setEditableFichajes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Manejar selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      setError('Solo se permiten imágenes JPG, JPEG o PNG');
      return;
    }

    // Validar tamaño (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen no puede superar los 10MB');
      return;
    }

    processImageFile(file);
  };

  // Procesar archivo de imagen (común para galería y cámara)
  const processImageFile = (file) => {
    setImagen(file);
    setError('');
    setPreviewData(null);
    setResultado(null);

    // Generar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Descargar plantilla PDF
  const descargarPlantilla = async () => {
    try {
      setError('');
      const response = await fetch(`${BACKEND_URL}/api/v1/formularios-manuscritos/plantilla`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error descargando plantilla');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla-fichaje-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      setError('Error descargando la plantilla: ' + err.message);
    }
  };

  // Vista previa del procesamiento
  const handlePreview = async () => {
    if (!imagen) {
      setError('Selecciona una imagen primero');
      return;
    }

    setProcesando(true);
    setError('');
    setPreviewData(null);
    setEditableFichajes([]);
    setResultado(null);

    try {
      // Cargar empleados para el selector
      const users = await getUsers(token);
      setEmpleados(users.filter(u => u.role === 'trabajador'));

      const formData = new FormData();
      formData.append('imagen', imagen);

      const response = await fetch(`${BACKEND_URL}/api/v1/formularios-manuscritos/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.mensaje || 'Error procesando formulario');
      }

      setPreviewData(data);
      // Inicializar estado editable a partir de los datos detectados
      setEditableFichajes((data.fichajes || []).map((f, i) => ({ ...f, _key: i })));

    } catch (err) {
      setError(err.message || 'Error procesando el formulario');
    } finally {
      setProcesando(false);
    }
  };

  // Actualizar un campo de un fichaje editable
  const updateFichaje = (index, field, value) => {
    setEditableFichajes(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      
      // Si se modifica el DNI, buscar automáticamente el usuario
      if (field === 'dni' && value.trim()) {
        const emp = empleados.find(e => e.dni && e.dni.toLowerCase() === value.trim().toLowerCase());
        if (emp) {
          copy[index] = {
            ...copy[index],
            dni: emp.dni,
            usuario: {
              id: emp._id,
              nombre: emp.nombre,
              apellidos: `${emp.primerApellido || ''} ${emp.segundoApellido || ''}`.trim(),
              email: emp.email,
              encontrado: true
            }
          };
        } else {
          // Si no se encuentra, marcar como no encontrado
          copy[index].usuario = { encontrado: false };
        }
      }
      
      return copy;
    });
  };

  // Seleccionar usuario desde el selector por _id
  const selectUsuario = (index, userId) => {
    const emp = empleados.find(e => e._id === userId);
    if (emp) {
      setEditableFichajes(prev => {
        const copy = [...prev];
        copy[index] = {
          ...copy[index],
          dni: emp.dni || copy[index].dni,
          usuario: {
            id: emp._id,
            nombre: emp.nombre,
            apellidos: `${emp.primerApellido || ''} ${emp.segundoApellido || ''}`.trim(),
            email: emp.email,
            encontrado: true
          }
        };
        return copy;
      });
    }
  };

  // Añadir fila vacía (al principio)
  const addFichaje = () => {
    setEditableFichajes(prev => [
      {
        _key: Date.now(),
        dni: '',
        fecha: '',
        horaEntrada: '',
        horaSalida: '',
        usuario: { encontrado: false }
      },
      ...prev
    ]);
  };

  // Eliminar fila
  const removeFichaje = (index) => {
    setEditableFichajes(prev => prev.filter((_, i) => i !== index));
  };

  // Confirmar y guardar los fichajes editados
  const handleConfirmar = async () => {
    if (!editableFichajes.length) return;

    // Validar que hay al menos un fichaje válido
    const validos = editableFichajes.filter(f => f.usuario?.encontrado && f.fecha && (f.horaEntrada || f.horaSalida));
    if (validos.length === 0) {
      setError('No hay ningún fichaje válido para guardar. Asegúrese de que cada fila tenga usuario, fecha y al menos una hora.');
      return;
    }

    setProcesando(true);
    setError('');
    setResultado(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/formularios-manuscritos/confirmar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fichajes: editableFichajes })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Error guardando fichajes');
      }

      setResultado(data);
      setPreviewData(null);
      setEditableFichajes([]);

    } catch (err) {
      setError(err.message || 'Error guardando los fichajes');
    } finally {
      setProcesando(false);
    }
  };

  // Procesar y crear fichajes
  const handleProcesar = async () => {
    if (!imagen) {
      setError('Selecciona una imagen primero');
      return;
    }

    setProcesando(true);
    setError('');
    setResultado(null);
    setEditableFichajes([]);

    try {
      const formData = new FormData();
      formData.append('imagen', imagen);

      const response = await fetch(`${BACKEND_URL}/api/v1/formularios-manuscritos/procesar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.mensaje || 'Error procesando formulario');
      }

      setResultado(data);
      setPreviewData(null);

    } catch (err) {
      setError(err.message || 'Error procesando el formulario');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <i className="fas fa-file-signature"></i>
          Escaneo de Formularios Manuscritos
        </h2>
        <p className="text-purple-100">
          Para trabajadores sin móvil: imprima la plantilla, que la rellenen a mano y escanee el formulario
        </p>
      </div>

      {/* Instrucciones */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <i className="fas fa-info-circle"></i>
          ¿Cómo funciona?
        </h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Descargue e imprima la plantilla PDF</li>
          <li>Los trabajadores rellenan sus datos A MANO con bolígrafo</li>
          <li>Tome una foto clara del formulario completado</li>
          <li>Suba la foto aquí y la IA extraerá los datos automáticamente</li>
          <li>Revise los datos detectados antes de crear los fichajes</li>
        </ol>
      </div>

      {/* Botón descargar plantilla */}
      <div className="flex justify-center">
        <button
          onClick={descargarPlantilla}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all transform hover:scale-105 shadow-lg"
        >
          <i className="fas fa-download"></i>
          Descargar Plantilla PDF
        </button>
      </div>

      {/* Selección de imagen */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="fas fa-camera"></i>
          Subir Formulario Completado
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Botón cámara nativa del móvil */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition transform hover:scale-105"
            disabled={procesando}
          >
            <i className="fas fa-camera"></i>
            Tomar Foto
          </button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Botón galería */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition transform hover:scale-105"
            disabled={procesando}
          >
            <i className="fas fa-folder-open"></i>
            Seleccionar de Galería
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Preview de imagen */}
        {preview && (
          <div className="mt-4">
            <div className="relative">
              <img 
                src={preview} 
                alt="Preview" 
                className="max-w-full h-auto rounded-lg border-2 border-gray-200 shadow"
              />
              <button
                onClick={() => {
                  setImagen(null);
                  setPreview(null);
                  setPreviewData(null);
                  setResultado(null);
                }}
                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-full text-sm shadow-lg"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Botones de acción */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <button
                onClick={handlePreview}
                disabled={procesando || !imagen}
                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition"
              >
                {procesando ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-eye"></i>
                    Vista Previa
                  </>
                )}
              </button>

              <button
                onClick={handleProcesar}
                disabled={procesando || !imagen}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition"
              >
                {procesando ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    Procesar y Crear Fichajes
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-circle text-red-500"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Preview de datos detectados - Editable */}
      {previewData && editableFichajes.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm md:text-base">
              <i className="fas fa-edit"></i>
              Revisar y Editar Datos ({editableFichajes.length})
            </h3>
            <button
              onClick={addFichaje}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium flex items-center gap-1 transition"
            >
              <i className="fas fa-plus"></i>
              <span className="hidden sm:inline">Añadir fila</span>
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            <i className="fas fa-info-circle mr-1"></i>
            Revise los datos detectados por la IA. Puede editar cualquier campo, cambiar el usuario o eliminar filas incorrectas.
          </p>

          <div className="space-y-4">
            {editableFichajes.map((fichaje, index) => (
              <div
                key={fichaje._key}
                className={`border rounded-lg p-3 md:p-4 relative ${
                  fichaje.usuario?.encontrado
                    ? 'border-green-200 bg-green-50/30'
                    : 'border-red-200 bg-red-50/30'
                }`}
              >
                {/* Cabecera de la tarjeta */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500">
                    #{index + 1}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {fichaje.usuario?.encontrado ? (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                        <i className="fas fa-check mr-1"></i>Válido
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                        <i className="fas fa-exclamation-triangle mr-1"></i>Sin usuario
                      </span>
                    )}
                    {!fichaje.fecha && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                        <i className="fas fa-calendar-exclamation mr-1"></i>Sin fecha
                      </span>
                    )}
                    {!fichaje.horaEntrada && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-700">
                        <i className="fas fa-clock mr-1"></i>Sin entrada
                      </span>
                    )}
                    <button
                      onClick={() => removeFichaje(index)}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition"
                      title="Eliminar fila"
                    >
                      <i className="fas fa-trash-alt text-sm"></i>
                    </button>
                  </div>
                </div>

                {/* Campos editables */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* DNI */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">DNI/NIE</label>
                    <input
                      type="text"
                      value={fichaje.dni || ''}
                      onChange={(e) => updateFichaje(index, 'dni', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500 transition"
                      placeholder="12345678A"
                    />
                  </div>

                  {/* Fecha */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${!fichaje.fecha ? 'text-yellow-600' : 'text-gray-600'}`}>Fecha {!fichaje.fecha && <i className="fas fa-exclamation-circle"></i>}</label>
                    <input
                      type="date"
                      value={fichaje.fecha || ''}
                      onChange={(e) => updateFichaje(index, 'fecha', e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500 transition ${!fichaje.fecha ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
                    />
                  </div>

                  {/* Hora Entrada */}
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${!fichaje.horaEntrada ? 'text-yellow-600' : 'text-gray-600'}`}>Entrada {!fichaje.horaEntrada && <i className="fas fa-exclamation-circle"></i>}</label>
                    <input
                      type="time"
                      value={fichaje.horaEntrada || ''}
                      onChange={(e) => updateFichaje(index, 'horaEntrada', e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-green-500 transition ${!fichaje.horaEntrada ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
                    />
                  </div>

                  {/* Hora Salida */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Salida</label>
                    <input
                      type="time"
                      value={fichaje.horaSalida || ''}
                      onChange={(e) => updateFichaje(index, 'horaSalida', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-500 transition"
                    />
                  </div>
                </div>

                {/* Selector de usuario */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Trabajador</label>
                  <select
                    value={fichaje.usuario?.id || ''}
                    onChange={(e) => selectUsuario(index, e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm transition focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500 ${
                      fichaje.usuario?.encontrado
                        ? 'border-green-300 bg-green-50'
                        : 'border-red-300 bg-red-50'
                    }`}
                  >
                    <option value="">-- Seleccionar trabajador --</option>
                    {empleados.map(emp => (
                      <option key={emp._id} value={emp._id}>
                        {emp.nombre} {emp.primerApellido} {emp.segundoApellido || ''} — {emp.dni || 'Sin DNI'}
                      </option>
                    ))}
                  </select>
                  {fichaje.usuario?.encontrado && (
                    <p className="text-xs text-green-600 mt-1">
                      <i className="fas fa-user-check mr-1"></i>
                      {fichaje.usuario.nombre} {fichaje.usuario.apellidos} ({fichaje.usuario.email})
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Resumen y acciones */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                <span className="text-green-600 font-medium">
                  {editableFichajes.filter(f => f.usuario?.encontrado).length} válido(s)
                </span>
                {' · '}
                <span className="text-red-600 font-medium">
                  {editableFichajes.filter(f => !f.usuario?.encontrado).length} sin usuario
                </span>
                {editableFichajes.filter(f => !f.fecha || !f.horaEntrada).length > 0 && (
                  <>
                    {' · '}
                    <span className="text-yellow-600 font-medium">
                      {editableFichajes.filter(f => !f.fecha || !f.horaEntrada).length} con avisos
                    </span>
                  </>
                )}
                {' · '}
                <span className="text-gray-500">
                  {editableFichajes.length} total
                </span>
              </div>

              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => { setPreviewData(null); setEditableFichajes([]); }}
                  className="flex-1 sm:flex-none px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmar}
                  disabled={procesando || editableFichajes.filter(f => f.usuario?.encontrado && f.fecha).length === 0}
                  className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-5 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition shadow-md text-sm"
                >
                  {procesando ? (
                    <><i className="fas fa-spinner fa-spin"></i> Guardando...</>
                  ) : (
                    <><i className="fas fa-save"></i> Confirmar y Guardar</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resultado del procesamiento */}
      {resultado && (
        <div className={`rounded-xl shadow-md p-6 ${resultado.success ? 'bg-green-50' : 'bg-red-50'}`}>
          <div className="flex items-start justify-between">
            <h3 className={`font-semibold mb-4 flex items-center gap-2 ${resultado.success ? 'text-green-800' : 'text-red-800'}`}>
              <i className={`fas ${resultado.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
              {resultado.mensaje}
            </h3>
            <button
              onClick={() => { setResultado(null); setImagen(null); setPreview(null); }}
              className="text-gray-400 hover:text-gray-600 text-sm px-2 py-1 rounded"
            >
              <i className="fas fa-times"></i> Cerrar
            </button>
          </div>

          {resultado.estadisticas && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{resultado.estadisticas.detectados}</div>
                <div className="text-xs text-gray-600">Fichajes en formulario</div>
              </div>
              {resultado.estadisticas.validos != null && (
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-600">{resultado.estadisticas.validos}</div>
                  <div className="text-xs text-gray-600">Trabajadores válidos</div>
                </div>
              )}
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{resultado.estadisticas.creados}</div>
                <div className="text-xs text-gray-600">Registros guardados</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{resultado.estadisticas.errores}</div>
                <div className="text-xs text-gray-600">Errores</div>
              </div>
            </div>
          )}

          {resultado.fichajesCreados && resultado.fichajesCreados.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700 mb-2">Fichajes Creados:</h4>
              <div className="space-y-2">
                {resultado.fichajesCreados.map((fichaje, index) => (
                  <div key={index} className="bg-white rounded p-3 text-sm">
                    <span className="font-medium">{fichaje.usuario}</span> - 
                    <span className="text-gray-600"> {fichaje.tipo} </span>
                    <span className="text-gray-500">el {fichaje.fecha} a las {fichaje.hora}</span>
                  </div>
                ))}
              </div>
              {onVerFichajes && (
                <button
                  onClick={() => {
                    const fechas = resultado.fichajesCreados.map(f => f.fecha).filter(Boolean);
                    const unicas = [...new Set(fechas)].sort();
                    if (unicas.length > 0) {
                      onVerFichajes({ from: unicas[0], to: unicas[unicas.length - 1] });
                    }
                  }}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 transition shadow"
                >
                  <i className="fas fa-calendar-check"></i> Ver en Control Horario
                </button>
              )}
            </div>
          )}

          {resultado.errores && resultado.errores.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold text-red-700 mb-2">Errores:</h4>
              <div className="space-y-2">
                {resultado.errores.map((error, index) => (
                  <div key={index} className="bg-white rounded p-3 text-sm text-red-600">
                    <span className="font-medium">DNI {error.dni}:</span> {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
